import {
  FanGetEngagementReactionsDocument,
  FanReactionFragment,
  FanSubmissionFragment,
  useCreateReactionMutation,
  useDeleteReactionMutation,
  useFanGetEngagementReactionsQuery,
  useVoteForVotesResetSubscription,
  useMockVoteForVotesResetSubscription,
  VoteForSubmissionData,
  VoteForViewConfig,
  VoteForViewData,
} from '@/generated/graphql';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from '@/styles/Bounce.module.css';
import Color from 'color';
import { useImageLoader } from '@/hooks/useImageLoader';
import pulseStyles from '@/styles/Pulse.module.css';
import { Loader } from '@/components/Loader';
import useWindowSize from '@/hooks/useWindowSize';
import { useFanEngagementSubmissions } from '@/hooks/useFanEngagementSubmissions';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import { cn } from '@/lib/utils';
import { TypedFanEngagement } from '@/utils/engagementTypeUtils';
import { useDeviceAccess } from '@/hooks/useDeviceAccess';
import { useDeviceSignalVariables } from '@/hooks/useDeviceIdentity';

export function FanVoteForEngagement({
  engagement,
}: {
  engagement: TypedFanEngagement<VoteForViewConfig, VoteForViewData>;
}) {
  const { data: submissionsData, loading: submissionsLoading } =
    useFanEngagementSubmissions({
      engagementId: engagement.id,
    });
  const { data: reactionsData, loading: reactionsLoading } =
    useFanGetEngagementReactionsQuery({
      variables: {
        engagementId: engagement.id,
      },
    });

  const mockRealtime = useMockRealtimeChannel();

  useVoteForVotesResetSubscription({
    variables: {
      engagementId: engagement.id,
    },
    onData: ({ client }) => {
      client.refetchQueries({
        include: [FanGetEngagementReactionsDocument],
      });
    },
  });
  useMockVoteForVotesResetSubscription({
    skip: !mockRealtime,
    variables: {
      engagementId: engagement.id,
    },
    onData: ({ client }) => {
      client.refetchQueries({
        include: [FanGetEngagementReactionsDocument],
      });
    },
  });

  const choices = useMemo(() => {
    if (submissionsLoading) {
      return [];
    }

    const submissions = submissionsData?.submissions ?? [];
    const submissionsById = submissions.reduce(
      (acc, submission) => {
        acc[submission.id] = submission;
        return acc;
      },
      {} as Record<string, FanSubmissionFragment>,
    );

    const choiceDisplayOrder =
      engagement.viewData?.choiceDisplayOrder || submissions.map((s) => s.id);
    return choiceDisplayOrder
      .map((submissionId) => {
        const submission = submissionsById[submissionId];
        if (!submission) {
          return null;
        }
        return {
          ...submission,
        };
      })
      .filter((c) => c !== null);
  }, [
    engagement.viewData?.choiceDisplayOrder,
    submissionsData?.submissions,
    submissionsLoading,
  ]);

  const { width } = useWindowSize();
  const choiceWidth = Math.min(width / 3, 100);
  const deviceAccess = useDeviceAccess(engagement.id);

  if (!deviceAccess.allowed) {
    return (
      <div className="flex min-h-[83vh] w-screen flex-col items-center justify-center p-8 text-center">
        <h1 className="font-screen-body text-2xl text-screen-foreground">
          {deviceAccess.message}
        </h1>
      </div>
    );
  }

  return (
    <div
      data-name="vote-for-container"
      className={`flex min-h-[83vh] w-screen flex-col items-center space-y-4`}
    >
      <div className="mb-4 flex flex-col items-center justify-center">
        <h1
          data-name="vote-for-title"
          className="px-8 text-center font-screen-body text-[6vw] text-screen-foreground"
        >
          {engagement.description}
        </h1>
      </div>
      {submissionsLoading && reactionsLoading ? (
        <Loader />
      ) : (
        <Choices
          choices={choices}
          choiceWidth={choiceWidth}
          engagement={engagement}
          engagementReactions={reactionsData?.engagementReactions ?? []}
        />
      )}
    </div>
  );
}

function Choices({
  choices,
  choiceWidth,
  engagementReactions,
  engagement,
}: {
  choices: FanSubmissionFragment[];
  choiceWidth: number;
  engagementReactions: FanReactionFragment[];
  engagement: TypedFanEngagement<VoteForViewConfig, VoteForViewData>;
}) {
  const [createReaction] = useCreateReactionMutation();
  const [deleteReaction] = useDeleteReactionMutation();
  const { variables: deviceSignals } = useDeviceSignalVariables();
  const voteInFlightRef = useRef(false);
  const [voteInFlight, setVoteInFlight] = useState(false);

  /**
   * Lookup of choiceId -> reactionId
   */
  const [chosen, setChosen] = useState<Record<string, string>>(() => {
    return engagementReactions.reduce(
      (acc, r) => {
        acc[r.submissionId] = r.id;
        return acc;
      },
      {} as Record<string, string>,
    );
  });

  useEffect(() => {
    if (voteInFlight) {
      return;
    }
    setChosen(
      engagementReactions.reduce(
        (acc, r) => {
          acc[r.submissionId] = r.id;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
  }, [engagementReactions, voteInFlight]);

  const votesPerUser = engagement.viewConfig?.votesPerUser ?? 1;
  const winners = engagement.viewData?.voteWinners ?? [];

  const handleChoiceClick = async (choice: FanSubmissionFragment) => {
    if (voteInFlightRef.current || winners.length > 0) {
      return;
    }

    voteInFlightRef.current = true;
    setVoteInFlight(true);
    try {
      if (chosen[choice.id]) {
        const reactionId = chosen[choice.id];
        setChosen((prev) => {
          const newChosen = { ...prev };
          delete newChosen[choice.id];
          return newChosen;
        });
        await deleteReaction({
          refetchQueries: [FanGetEngagementReactionsDocument],
          variables: {
            reactionId,
          },
        });
      } else if (
        votesPerUser === 0 ||
        Object.keys(chosen).length < votesPerUser
      ) {
        const { data } = await createReaction({
          refetchQueries: [FanGetEngagementReactionsDocument],
          variables: {
            submissionId: choice.id,
            type: 'upvote',
            ...deviceSignals,
          },
        });
        if (data?.createReaction) {
          setChosen((prev) => {
            const newChosen = { ...prev };
            newChosen[choice.id] = data.createReaction.id;
            return newChosen;
          });
        }
      }
    } finally {
      voteInFlightRef.current = false;
      setVoteInFlight(false);
    }
  };
  return (
    <div
      data-name="choices"
      aria-busy={voteInFlight}
      className={cn(
        'flex flex-wrap justify-center',
        voteInFlight && 'pointer-events-none',
      )}
    >
      {choices.map((choice) => (
        <Choice
          key={choice.id}
          choice={choice}
          handleClick={handleChoiceClick}
          isChosen={chosen[choice.id] !== undefined}
          width={choiceWidth}
          isWinner={winners.includes(choice.id)}
          isLoser={winners.length > 0 && !winners.includes(choice.id)}
        />
      ))}
    </div>
  );
}

const Choice = ({
  choice,
  handleClick,
  isChosen,
  width,
  isWinner,
  // isLoser,
}: {
  choice: FanSubmissionFragment;
  handleClick: (choice: FanSubmissionFragment) => void;
  isChosen: boolean;
  width: number;
  isWinner: boolean;
  isLoser: boolean;
}) => {
  const data = choice.data as VoteForSubmissionData;
  const { imageLoaded: hasImageLoaded } = useImageLoader({
    url: data.optionalImageUrl ?? undefined,
  });
  const imageLoaded = !data.optionalImageUrl || hasImageLoaded;
  const borderColor = useMemo(
    () => (isChosen ? data.color : Color(data.color).desaturate(0.75).hex()),
    [data.color, isChosen],
  );
  const imageStyles = useMemo(
    () => ({
      border: `4px solid ${borderColor}`,
      width,
      height: width,
      background: data.optionalImageUrl
        ? `url(${data.optionalImageUrl})`
        : `${data.color}`,
      backgroundSize: 'cover',
    }),
    [borderColor, width, data.optionalImageUrl, data.color],
  );

  const titleLength = data.title.length;
  // Shrink earlier than the old >10 threshold so labels like Magenta/Orange/Purple
  // fit inside the fixed-width pill on narrow phones.
  const titleTextClass =
    titleLength > 12
      ? 'text-[2.2vw]'
      : titleLength >= 6
        ? 'text-[2.8vw]'
        : 'text-[3.5vw]';

  return (
    <div
      data-name={`CHOICE_${choice.id}`}
      key={choice.id}
      style={{ width }}
      className={cn(
        'relative mb-8 ml-[5vw] mr-[5vw] transition-all',
        imageLoaded
          ? cn(isChosen ? 'opacity-100' : 'opacity-70', pulseStyles.pulse)
          : 'opacity-20',
      )}
      onClick={() => handleClick(choice)}
    >
      <div
        data-name="CHOICE_TITLE_MOBILE"
        style={{
          boxShadow: `${isChosen ? `5px -5px` : `3px -3px`} 0 ${borderColor}`,
          width,
          maxWidth: width,
        }}
        className={cn(
          'relative z-10 rounded-lg bg-white text-black',
          'box-border px-1.5 py-1 text-center font-screen-body',
          'overflow-hidden break-words leading-tight [overflow-wrap:anywhere]',
          titleTextClass,
          isChosen ? styles.bounce : '',
        )}
      >
        <span
          className={`absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 text-2xl transition-all ${isWinner ? 'scale-100 opacity-100' : 'scale-[300%] opacity-0'}`}
        >
          ⭐️
        </span>
        <span className="block max-w-full">{data.title}</span>
      </div>
      <div
        data-name="CHOICE_IMAGE_CTNR_MOBILE"
        className={`relative flex w-full items-center justify-center rounded-full bg-muted`}
      >
        {!imageLoaded && (
          <Loader
            className={`absolute flex h-full w-full items-center justify-center`}
          />
        )}
        <div
          data-name="CHOICE_IMAGE_MOBILE"
          style={imageStyles}
          className={`rounded-full transition-opacity ${imageLoaded ? 'opacity-100' : `opacity-0`} ${isChosen && imageLoaded ? styles.bounce : ''} `}
        />
      </div>
    </div>
  );
};
